import { useState, useEffect, ReactNode } from 'react';
import { redirectToCustomerLogin } from '../lib/sso';

const SITE = 'https://podstack.ai';
const PRODUCTS = [
  { label: 'QuickPods', href: `${SITE}/products/quickpods` },
  { label: 'TrainPods', href: `${SITE}/products/trainpods` },
  { label: 'Inference', href: `${SITE}/products/inference` },
  { label: 'DC Suite', href: `${SITE}/products/dc-suite` },
];
const RESOURCES = [
  { label: 'Docs', href: 'https://docs.podstack.ai' },
  { label: 'Blog', href: 'https://blog.podstack.ai' },
];

// CSS-transition dropdown (no framer-motion — incompatible with this app's
// TypeScript 4.9 + @types/react 19).
function Dropdown({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        className="inline-flex items-center gap-1 text-sm text-ps-muted transition-colors hover:text-ps-text"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((o) => !o)}
      >
        {label}
        <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none">
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </button>
      <div
        className={`absolute left-0 mt-2 min-w-[180px] rounded-xl border border-ps-strong bg-ps-card p-2 shadow-xl shadow-black/50 transition-all duration-150 ${
          open ? 'visible translate-y-0 opacity-100' : 'invisible -translate-y-1 opacity-0'
        }`}

      >
        {children}
      </div>
    </div>
  );
}

export default function SiteNav({
  getStartedLabel = 'Get Started',
  onGetStarted = redirectToCustomerLogin,
}: {
  getStartedLabel?: string;
  onGetStarted?: () => void;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const itemCls =
    'block rounded-md px-3 py-2 text-sm text-ps-text transition-colors hover:bg-ps-raised';
  return (
    <nav
      aria-label="Main navigation"
      className="fixed left-0 right-0 top-0 z-50 border-b border-ps-raised bg-ps-chrome/90 backdrop-blur-xl"
    >
      <div className="mx-auto max-w-7xl px-6 py-4">
        <div className="flex items-center justify-between">
          <a href={SITE} className="flex items-center gap-2 transition-opacity hover:opacity-90">
            <img src={`${process.env.PUBLIC_URL}/logo.svg`} alt="" aria-hidden="true" className="h-7 w-7" />
            <span className="font-display text-[17px] font-semibold text-ps-text">Podstack Cloud</span>
          </a>

          <div className="hidden items-center gap-6 md:flex">
            <Dropdown label="Products">
              {PRODUCTS.map((p) => (
                <a key={p.href} href={p.href} className={itemCls}>
                  {p.label}
                </a>
              ))}
              <a href={`${SITE}/#products`} className={itemCls}>
                All products →
              </a>
            </Dropdown>
            <a href={`${SITE}/#pricing`} className="text-sm text-ps-muted transition-colors hover:text-ps-text">
              Pricing
            </a>
            <a href={`${SITE}/object-storage`} className="text-sm text-ps-muted transition-colors hover:text-ps-text">
              Storage
            </a>
            <Dropdown label="Resources">
              {RESOURCES.map((r) => (
                <a key={r.href} href={r.href} target="_blank" rel="noopener noreferrer" className={itemCls}>
                  {r.label}
                </a>
              ))}
            </Dropdown>
            <a href={`${SITE}/about`} className="text-sm text-ps-muted transition-colors hover:text-ps-text">
              Company
            </a>
            <a href="mailto:sales@podstack.ai" className="text-sm text-ps-muted transition-colors hover:text-ps-text">
              Contact
            </a>
            <button onClick={onGetStarted} className="ps-btn ps-btn-sm">
              {getStartedLabel}
            </button>
          </div>

          <button
            onClick={() => setMobileOpen((o) => !o)}
            className="grid h-11 w-11 place-items-center rounded-md border border-ps-strong bg-ps-raised text-ps-text hover:bg-ps-border md:hidden"
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {mobileOpen && (
          <div className="mt-4 border-t border-ps-raised pt-3 md:hidden">
            <div className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-ps-subtle">Products</div>
            {PRODUCTS.map((p) => (
              <a key={p.href} href={p.href} className={itemCls}>
                {p.label}
              </a>
            ))}
            <a href={`${SITE}/#pricing`} className={itemCls}>
              Pricing
            </a>
            <a href={`${SITE}/object-storage`} className={itemCls}>
              Storage
            </a>
            <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-ps-subtle">Resources</div>
            {RESOURCES.map((r) => (
              <a key={r.href} href={r.href} target="_blank" rel="noopener noreferrer" className={itemCls}>
                {r.label}
              </a>
            ))}
            <a href={`${SITE}/about`} className={itemCls}>
              Company
            </a>
            <a href="mailto:sales@podstack.ai" className={itemCls}>
              Contact
            </a>
            <button onClick={onGetStarted} className="ps-btn mx-3 mt-2 flex w-[calc(100%-1.5rem)]">
              {getStartedLabel}
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}
